/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-runloop, ember/require-tagless-components */
import Component from '@ember/component';
import { scheduleOnce } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import { attributeBindings, classNames } from '@ember-decorators/component';

// D3 imports
import { select } from 'd3-selection';
import { scaleLinear, scaleOrdinal } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { axisBottom, axisLeft } from 'd3-axis';
import { line as d3Line } from 'd3-shape';
import tip from '@linc-technologies/linced-common/utils/d3-tip';
import { mapBy } from '@linc-technologies/linced-common/utils/arrays';

@classNames('chart', 'chart-line', 'layout-row', 'layout-align-start-center')
@attributeBindings('width', 'height')
export default class ChartLine extends Component {
  width = 480;
  height = 320;
  margin = Object.freeze({ top: 20, right: 30, bottom: 30, left: 30 });
  showLegend = true;
  max = null;

  getTip(d) {
    return `${d.label}: ${d.value}`;
  }

  // Optional: larger dots with a white outline that grow on hover, easier to
  // see and to click. Off by default, so existing charts are unchanged.
  emphasiseDots = false;

  // Optional: angles the x-axis labels so long ones don't run into each other
  // on a narrow chart. Callers allow for the taller labels with a larger bottom
  // margin. Off by default, so existing charts are unchanged.
  angleXLabels = false;

  // Optional: called with a point's datum when its dot is clicked. Without it
  // the dots are hover-only, as is any point whose datum sets clickable: false.
  onDotClick = null;

  _canClickDot(d) {
    return Boolean(this.onDotClick) && d.clickable !== false;
  }

  colors = schemeCategory10;
  data = [];

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Schedule a call to our `draw` method on Ember's "render" queue, which will
    // happen after the component has been placed in the DOM, and subsequently
    // each time data is changed.
    scheduleOnce('render', this, this.draw);
  }

  getMaxValue() {
    return this.data.reduce((max, line) => {
      let lineMax = line.data.reduce((max, point) => {
        return Math.max(max, point.value);
      }, 0);
      return Math.max(max, lineMax);
    }, 0);
  }

  draw() {
    let plot = select(this.element.querySelector('svg'));
    let legendElement = this.element.querySelector('.legend');
    let legend = select(legendElement);

    let data = this.data;
    if (isEmpty(data)) {
      // No point trying to chart an empty data set.
      return;
    }

    let margin = this.margin;
    let width = this.width - margin.left - margin.right;
    let height = this.height - margin.top - margin.bottom;

    // If we're showing a legend, remove its width from the available width (if needed) so that we don't overlap.
    if (this.showLegend) {
      let containerWidth = this.element.clientWidth;
      let legendWidth = legendElement.clientWidth;
      if (containerWidth < width + legendWidth) {
        width -= legendWidth;
        plot.attr('width', this.width - legendWidth);
      }
    }

    let keys = mapBy(data[0].data, 'label');
    let max = isEmpty(this.max) ? this.getMaxValue() : this.max;

    let xStep = Math.floor(width / (keys.length - 1));
    let xRange = keys.map((_, idx) => idx * xStep);

    let x = scaleOrdinal().range(xRange),
      y = scaleLinear().range([height, 0]),
      color = scaleOrdinal(this.colors);

    // A point with a null value has no data: the line breaks there and no dot
    // is drawn, rather than it reading as a zero.
    let hasValue = (d) => d.value !== null && d.value !== undefined;
    let line = d3Line()
      .defined(hasValue)
      .x((d) => x(d.label))
      .y((d) => y(d.value));

    // Remove previous chart (if there is one), then append the new root element.
    plot.selectAll('*').remove();
    let g = plot
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    x.domain(keys);
    y.domain([0, max]);
    color.domain(keys);

    // Tooltip
    let dotTip = tip()
      .attr('class', 'd3-tip')
      .html((d) => this.getTip(d));
    g.call(dotTip);

    let lines = g
      .selectAll('.layer-line')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'layer-line')
      .append('g');

    lines
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', (d) => d.color || color(d.label))
      .attr('stroke-width', 1)
      .attr('d', (d) => line(d.data));

    let emphasise = this.emphasiseDots;
    let radius = (d) => {
      let base = emphasise ? 4 : 3;
      return d.highlight ? base + 2 : base;
    };

    lines
      .selectAll('circle-group')
      .data(data)
      .enter()
      .append('g')
      .style('fill', (d) => d.color || color(d.label))
      .selectAll('.dot')
      .data((d) => d.data.filter(hasValue))
      .enter()
      .append('circle')
      .attr('class', 'dot')
      .attr('cx', (d) => x(d.label))
      .attr('cy', (d) => y(d.value))
      // A datum can set highlight to stand out from the rest of its line.
      .attr('r', radius)
      .attr('stroke', emphasise ? '#fff' : null)
      .attr('stroke-width', emphasise ? 1.5 : null)
      .style('cursor', (d) => (this._canClickDot(d) ? 'pointer' : null))
      .on('mouseover', function (event, d) {
        dotTip.show(event, d, this);
        if (emphasise) {
          select(this).attr('r', radius(d) + 1.5);
        }
      })
      .on('mouseout', function (event, d) {
        dotTip.hide();
        if (emphasise) {
          select(this).attr('r', radius(d));
        }
      })
      .on('click', (event, d) => {
        if (this._canClickDot(d)) {
          dotTip.hide();
          this.onDotClick(d);
        }
      });

    let xAxis = g
      .append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', 'translate(0,' + height + ')')
      .call(axisBottom(x));
    if (this.angleXLabels) {
      xAxis
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-0.6em')
        .attr('dy', '0.15em')
        .attr('transform', 'rotate(-40)');
    }

    // Axis labels for highlighted points are bold too, so the highlight reads
    // along the axis as well as on the lines.
    let highlighted = new Set();
    data.forEach((series) =>
      series.data.forEach((point) => {
        if (point.highlight) {
          highlighted.add(point.label);
        }
      }),
    );
    g.selectAll('.axis--x .tick text').style('font-weight', (label) =>
      highlighted.has(label) ? 'bold' : null,
    );

    g.append('g')
      .attr('class', 'axis axis--y')
      .call(axisLeft(y).ticks(Math.min(5, max)));

    if (this.showLegend) {
      let legendItem = legend.selectAll('li').data(data).enter().append('li');

      legendItem
        .append('span')
        .attr('class', 'legend-chip')
        .attr(
          'style',
          (d) =>
            `box-shadow: inset 0px 0px 0px 10px ${d.color || color(d.label)}`,
        );

      legendItem.append('span').text((d) => d.label);
    }
  }
}
